<template lang="pug">
.k-video-player
  transition( name='fade' mode='out-in' appear )
    video-player(
      ref='player'
      :playsinline='true'
      :options='playerOptions'
      @play='onPlay'
      @pause='onPause'
      @ended='onEnded'
      @waiting='onWaiting'
      @playing='onPlaying'
      @loadeddata='onLoadeddata'
      @timeupdate='onTimeupdate'
      @canplay='onCanplay'
      @canplaythrough='onCanplaythrough'
      @statechanged='onStatechanged'
      @ready='onReady'
    )
  .video-overlay( ref='overlay' @click='onOverlayClick' )
  d-video-info
  w-ctrl-video(
    v-bind=`{ player, playTime, playlist, index, paused, muted, volume }`
    @play='play'
    @pause='pause'
    @mute='mute'
    @unmute='unmute'
    @volume='setVolume'
    @seek='seek'
  )
</template>

<script lang="js">
import 'video.js/dist/video-js.css'
import AnimatedValue from 'animated-value'
import { mapState, mapActions } from 'vuex'
import { videoPlayer } from 'vue-video-player'
import DVideoInfo from '@/displays/d-video-info'
import WCtrlVideo from '@/widgets/w-ctrl-video'
import { Logger } from '@/lib/logger'
import { mediaActions } from '@/mixins'

export default {
  name: 'VideoPlayerBlock',
  mixins: [ mediaActions ],
  components: { videoPlayer, DVideoInfo, WCtrlVideo },
  props: {
    series: Array,
    index: {
      type: Number,
      default: 0
    },
    content: {
      type: Object,
      default: () => ({})
    },
    options: {
      type: Object,
      default: () => ({
        controls: false,
        autoplay: false,
        playsinline: true,
        aspectRatio: '16:9',
        muted: false,
        controlBar: {
          // children: [
          //   'playToggle',
          //   'volumeMenuButton',
          //   'currentTimeDisplay',
          //   'durationDisplay',
          //   'progressControl',
          //   'remainingTimeDisplay',
          //   'playbackRateMenuButton',
          //   'subtitlesButton',
          //   'captionsButton',
          //   'fullscreenToggle'
          // ],
        }
      })
    }
  },
  data() {
    return {
      player: null,
      playTime: 0,
      paused: true,
      muted: false,
      volume: 1,
    }
  },
  created() {},
  mounted() {},
  methods: {
    play() {
      if ( !this.player ) return
      this.player.play()
    },
    pause() {
      if ( !this.player ) return
      this.player.pause()
    },
    async crossFade( start, end, duration = 600) {
      const ease = AnimatedValue.CURVES.EASE_IN_OUT
      const crossVol = new AnimatedValue({ start, end, ease })
      const interval = setInterval(
        () => { this.player.volume( crossVol.value() )},
        160
      )
      return crossVol.play( duration ).then(() => {
        clearInterval( interval )
      })
    },
    async mute() {
      if ( !this.player ) return
      this.muted = true
      const vol = this.volume
      await this.crossFade( vol, 0 )
      this.player.muted( true )
      this.player.volume( vol )
      this.player.muted( true )
    },
    async unmute() {
      if ( !this.player ) return
      this.muted = false
      this.player.muted( false )
      this.crossFade( 0, this.volume )
    },
    async setVolume( vol ) {
      if ( !this.player ) return
      const oldVol = this.volume
      this.volume = vol
      this.crossFade( oldVol, vol, 1200 )
    },
    seek( time ) {
      if ( !this.player ) return
      this.player.currentTime( time )
    },
    back() {
      this.$emit( 'back' )
    },
    next() {
      this.$emit( 'next' )
    },
    onPlay( player ) {
      Logger.debug( '@play', '<k-video-player>', { player, id: player.id_ })()
      this.paused = false
      this.$emit( 'play', player )
    },
    onPause( player ) {
      Logger.debug( '@pause', '<k-video-player>', { player, id: player.id_ })()
      this.paused = true
      this.$emit( 'pause', player )
    },
    onEnded( player ) {
      Logger.debug( '@ended', '<k-video-player>', { player, id: player.id_ })()
      this.paused = true
      this.$emit( 'ended', player )
    },
    onWaiting( player ) {
      Logger.debug( '@waiting', '<k-video-player>', { player, id: player.id_ })()
      this.$emit( 'waiting', player )
    },
    onPlaying( player ) {
      Logger.debug( '@playing', '<k-video-player>', { player, id: player.id_ })()
      this.$emit( 'playing', player )
    },
    onLoadeddata( player ) {
      Logger.debug( '@loadeddata', '<k-video-player>', { player, id: player.id_ })()
      this.$emit( 'loadeddata', player )
    },
    onTimeupdate( player ) {
      // Logger.debug( '@timeupdate', '<k-video-player>', { player, id: player.id_ })()
      // this.$emit( 'timeupdate', player )
    },
    onCanplay( player ) {
      Logger.debug( '@canplay', '<k-video-player>', { player, id: player.id_ })()
      this.$emit( 'canplay', player )
    },
    onCanplaythrough( player ) {
      Logger.debug( '@canplaythrough', '<k-video-player>', { player, id: player.id_ })()
      this.$emit( 'canplaythrough', player )
    },
    onStatechanged( $event ) {
      // Logger.debug( '@statechanged', '<k-video-player>', { player, id: player.id_ })()
      // this.$emit( 'statechanged', player )
      if ( 'timeupdate' in $event ) {
        this.playTime = $event.timeupdate
      }
    },
    onReady( player ) {
      Logger.debug( 'onReady', '<k-video-player>', { player, id: player.id_ })()
      this.player = player
      if ( process.env.NODE_ENV === 'development' ) {
        setTimeout(() => { player.pause() }, 500 )
      }
      this.playVolume = player.volume()
      this.$emit( 'ready', player )
    },
    onOverlayClick( $event ) {
      const overlay = this.$refs.overlay
      if ( $event.target === overlay ) {
        if ( this.paused ) {
          this.play()
        } else {
          this.pause()
        }
      }
    },
  },
  computed: {
    playerOptions() {
      return this.videoPlayerOptions( this.content, this.options )
    },
    playlist() {
      return this.series && this.series.length ? this.series : [ this.content ]
    }
  }
}
</script>

<style lang="scss">
@import "@/scss/breakpoint";
.k-video-player {
  &.fullscreen {
    position: relative;
    width: 100%;
    height: 100%;
    top: 0%;
    left: 0%;
    background: black;
  }
  > .video-player {
    position: relative;
    width: 100%;
    height: 100%;
  }
  .video-js {
    position: relative;
    width: 100%;
    height: 100%;
    top: 0%;
    left: 0%;
    padding: 0;
  }
  .vjs-tech {
    position: relative;
    width: 100%;
    /* height: 90%; */
    /* top: 50%; */
    /* left: 50%; */
    min-width: 100%;
    min-height: 90%;
    /* transform: translate(-50%,0); */
    object-fit: contain;
  }
  .video-overlay {
    position: absolute;
    width: 100%;
    height: 100%;
    top: 50%;
    left: 50%;
    transform: translate(-50%,-50%);
  }
  > .w-ctrl-video {
    position: absolute;
    bottom: var(--s-base);
    left: var(--s-base);
    width: var(--w-ctrl-video, calc( 100% - 2 * var(--s-base)));
    max-width: var(--w-max-ctrl-video, 100%);
    transition-property: width,height,opacity;
    transition-duration: 400ms;
    opacity: 0.1;
    .w-btn:hover {
      background: transparent;
    }
    @include for-desktop {
      .buttons {
        margin-right: var(--s-xl);
      }
    }
  }
  &:hover > .w-ctrl-video {
    opacity: 1;
  }
}
</style>
